import React from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { selectLicenseInfo } from 'software_renewal/redux/selectors';

/**
 * Component that displays a message when a complimentary serial has already been applied.
 * Only renders if complementedBy is present in the license info.
 * Returns null if expiration is within 30 days (allowing ProductsToPurchase to show instead).
 */
const ComplimentarySerialMessage = () => {
  const licenseInfo = useSelector(selectLicenseInfo);

  // Validate that we have the required data
  if (!licenseInfo?.complementedBy) {
    return null;
  }

  const { customerEmail, complementedBy, serialNumber } = licenseInfo;

  // Validate complementedBy is an object with expected structure
  if (typeof complementedBy !== 'object' || complementedBy === null) {
    return null;
  }

  // Validate we have a customer email to display (already masked from backend)
  if (!customerEmail) {
    return null;
  }

  const complimentarySerial = complementedBy?.Serial;
  const complimentaryExpiration = complementedBy?.Expiration;
  const emailSentDate =
    complementedBy?.EmailSentDate || complementedBy?.email_sent_date;

  // Check if expiration is within 30 days - if so, don't show this message
  // ProductsToPurchase will be shown instead
  if (complimentaryExpiration) {
    const expirationDate = dayjs(complimentaryExpiration);
    const daysUntilExpiration = expirationDate.diff(dayjs(), 'day');
    if (daysUntilExpiration <= 30) {
      return null;
    }
  }

  // Build message for contact form
  const message = `Hello,\n\nI would like to get more information about my complimentary serial number (Serial: ${serialNumber || complimentarySerial}).\n\nCould you please provide me with additional details?\n\nThank you.`;
  const contactUsLink = `/complimentary-serial-request?msg=${encodeURIComponent(message)}`;

  return (
    <div className="card product-listing p-4">
      <div className="complimentary-serial-message">
        <div className="row">
          <div className="col-12">
            <h4 className="font-heavy mb-3">
              Complimentary Serial Already Applied
            </h4>
            <p className="mb-3">
              We&apos;re pleased to inform you that your serial number qualifies
              for a complimentary ViewMate Professional upgrade at no additional
              cost. The upgrade has already been applied to your account.
            </p>
            {complimentarySerial && (
              <p className="mb-2">
                <strong>Complimentary Serial Number:</strong>{' '}
                {complimentarySerial}
              </p>
            )}
            {complimentaryExpiration && (
              <p className="mb-3">
                <strong>Expiration Date:</strong>{' '}
                {dayjs(complimentaryExpiration).format('MMMM D, YYYY')}
              </p>
            )}
            <p className="mb-3">
              A confirmation email with your complimentary serial number and
              password, along with activation instructions, was sent previously
              {emailSentDate && (
                <> on {dayjs(emailSentDate).format('MMMM D, YYYY')} to</>
              )}
              {!emailSentDate && <> to</>} <strong>{customerEmail}</strong>.
              Please check your inbox (and spam folder) for that email.
            </p>
            <p className="mb-3">
              If you no longer have that email, please visit the{' '}
              <a href="/serial-recovery">Serials Recovery page</a> to have the
              complimentary license email resent to you, or{' '}
              <a href={contactUsLink}>contact support</a> for assistance.
            </p>
            <p className="mb-0">
              After receiving your complimentary license serial number and
              password, you can visit the{' '}
              <a href="https://activationkeys.pentalogix.com/" target="_blank" rel="noreferrer">
                Activation Keys page
              </a>{' '}
              to generate your activation key.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplimentarySerialMessage;
